import { Checkbox, Field, Text } from "@carbon6/c6-frontend-design";
import type { ChangeEvent, ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import styles from "./submitWizard.module.css";

/**
 * 제출 폼의 필드 프리미티브. 라벨·힌트·필수 표시를 한 묶음으로 만든다.
 *
 * 위저드 스텝(`SubmitWizardSteps`)과 Schedule 편집 다이얼로그가 같은 형태의 필드를 쓴다.
 * 스텝 모듈에 두면 라벨 래퍼 하나를 쓰려고 저장소에서 가장 큰 모듈 전체를 import 하게 된다.
 * 치수는 `submitWizard.module.css` 가 소유한다. 이 파일은 조립만 한다.
 */

// 필수 선택 표시. 스크린리더는 '필수'로 읽고, 화면에는 강조 * 로 표시한다.
function RequiredMark(): ReactNode {
  return (
    <em className={styles.requiredMark} aria-label="필수">
      *
    </em>
  );
}

// 라벨이 붙은 입력 한 묶음. 위저드의 모든 필드가 같은 타이포(caption-bold 라벨 · caption 힌트)와
// 같은 간격을 쓴다. 호출부가 라벨/힌트를 각자 조립하면 크기·여백이 필드마다 달라진다.
export function SubmitField({
  label,
  required = false,
  hint,
  dividerTop = false,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  // 앞 필드와 나누는 구분선을 추가한다. 묶음의 첫 필드는 끄고 두 번째부터 켠다.
  dividerTop?: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <div className={dividerTop ? `${styles.field} ${styles.fieldDividerTop}` : styles.field}>
      {/* 라벨과 컨트롤이 한 묶음이고 힌트는 그 묶음에 붙는 부속이다. 셋을 같은 간격으로 쌓으면
          힌트가 다음 필드만큼 떨어져 보여서, 사용자가 힌트가 설명하는 컨트롤을 한 번 더 찾아야 한다. */}
      <div className={styles.fieldControl}>
        {/* 라벨은 body 다. caption 은 값보다 작아서, 무엇을 고르는 자리인지보다 값이 먼저 읽힌다.
            섹션 제목(title)·필드 라벨(body)·힌트(caption) 세 단으로 위계를 나눈다. */}
        <Text variant={hatcheryTextModes["body-bold"]}>
          {label}
          {required ? <RequiredMark /> : null}
        </Text>
        {children}
      </div>
      {hint === undefined ? null : <p className={styles.hint}>{hint}</p>}
    </div>
  );
}

// on/off 노브 한 줄. c6 Field 가 체크박스를 라벨 왼쪽에 두고 id 연결을 소유한다
// (layout="hug" + labelPlacement="end" = control 먼저). 설명은 라벨 오른쪽 힌트로 표시한다.
//
// 라벨은 body 다. Checkbox 의 기본 크기가 `1.7em` 이라 주변 폰트에 비례한다. caption(12px)과
// 함께 쓰면 상자만 커 보인다. 그래서 c6 는 체크박스+라벨 조합에 body 를 쓰라고 문서화한다.
export function ToggleField({
  label,
  hint,
  hintPlacement = "inline",
  checked,
  disabled = false,
  onChange,
}: {
  label: string;
  // 켜고 끈 결과 또는 켤 수 없는 이유. 설명할 내용이 없는 상태는 비운다.
  hint?: string;
  // 힌트 위치. 기본은 라벨 오른쪽 한 줄이다. "below" 면 체크박스 아래 줄이다(라벨이 길어서 옆에
  // 붙이면 힌트가 여러 줄로 접히는 경우).
  hintPlacement?: "inline" | "below";
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}): ReactNode {
  return (
    <div
      className={
        hintPlacement === "below"
          ? `${styles.toggleField} ${styles.toggleFieldStacked}`
          : styles.toggleField
      }
    >
      <Field
        label={label}
        layout="hug"
        labelPlacement="end"
        disabled={disabled}
        textVariant={hatcheryTextModes["body-normal"]}
      >
        <Checkbox
          checked={checked}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
        />
      </Field>
      {hint === undefined ? null : <p className={styles.hint}>{hint}</p>}
    </div>
  );
}
